import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

function refresh(containerElement: HTMLElement, resizeToFit?: boolean) {
  // If the web-publishing class is present, we are publishing a shared webpage and don't need to resize
  if (
    !resizeToFit ||
    document.getElementsByClassName('web-publishing').length > 0
  ) {
    return;
  }

  const isPdf = /HeadlessChrome/.test(navigator.userAgent);

  if (isPdf) {
    console.log('ScalingReport refresh: Running in PDF/Headless mode');
  }

  const containerWidth = containerElement.offsetWidth;

  if (!containerWidth) {
    isPdf &&
      console.log(
        'ScalingReport refresh: No container width detected, skipping'
      );
    return;
  }

  const contentElement = containerElement.querySelector<HTMLElement>(
    ':scope .embedded-content-container'
  );

  if (contentElement) {
    // Find the difference between the left sides of the container and the content
    // to account for margin/padding
    const containerBounds = containerElement.getBoundingClientRect();
    const contentBounds = contentElement.getBoundingClientRect();
    const leftOffset = Math.ceil(contentBounds.left - containerBounds.left);

    // Log dimensions for debugging PDF issues
    if (isPdf) {
      console.log('ScalingReport dimensions:', {
        containerWidth,
        containerHeight: containerElement.offsetHeight,
        contentWidth: contentElement.offsetWidth,
        contentHeight: contentElement.offsetHeight,
        leftOffset,
        contentBounds: {
          width: contentBounds.width,
          height: contentBounds.height,
          left: contentBounds.left,
          top: contentBounds.top,
        },
        containerBounds: {
          width: containerBounds.width,
          height: containerBounds.height,
          left: containerBounds.left,
          top: containerBounds.top,
        },
      });
    }

    // Assume element is centered - comparing bounds doesn't work if table
    // exceeds the width of container.
    const effectiveWidth = containerWidth - 2 * leftOffset;
    const scale = effectiveWidth / contentElement.offsetWidth;

    if (isPdf) {
      console.log('ScalingReport calculation:', {
        effectiveWidth,
        calculatedScale: scale,
      });
    }

    if (scale >= 1.0) {
      isPdf && console.log('ScalingReport: Scale >= 1.0, no scaling needed');
      return;
    }

    const newStyle: Partial<CSSStyleDeclaration> = {};

    // Avoid percentage format for transform scale to prevent potential issues with Puppeteer
    newStyle.transform = `scale(${scale})`;
    newStyle.transformOrigin = `left top`;

    // Scaling leaves content height unchanged - use margin bottom to hide whitespace
    let contentHeight = Math.ceil(contentElement.offsetHeight * scale);
    newStyle.marginBottom = `-${contentElement.offsetHeight - contentHeight}px`;

    if (isPdf) {
      console.log('ScalingReport applying styles:', {
        scale,
        contentHeight,
        marginBottom: newStyle.marginBottom,
        transform: newStyle.transform,
      });
    }

    // Only apply styles if they've changed
    Object.entries(newStyle).forEach(([key, value]) => {
      if (contentElement.style[key] !== value) {
        contentElement.style[key] = value;
      }
    });

    // Force the height of the content
    const newContentHeight = `${contentHeight}px`;
    if (newContentHeight !== contentElement.parentElement.style.height) {
      contentElement.parentElement.style.height = newContentHeight;
    }

    // For PDF rendering, 'contain: strict' can cause layout issues
    // Only apply it for web rendering, not for PDF generation
    contentElement.parentElement.style.contain = isPdf ? 'content' : 'strict';
  } else if (isPdf) {
    console.log('ScalingReport: No embedded-content-container found');
  }
}

interface ScalingReportProps {
  fullSize?: boolean;
  resizeToFit?: boolean;
  content: ReactNode;
}

const ScalingReport: React.FC<ScalingReportProps> = ({
  fullSize,
  resizeToFit,
  content,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const refreshHandler = useCallback(() => {
    if (containerRef.current) {
      refresh(containerRef.current, resizeToFit);
    }
  }, [resizeToFit]);
  const resizeObserver = useState(() => new ResizeObserver(refreshHandler))[0];
  const style: React.CSSProperties = { height: '100%', position: 'relative' };

  useEffect(() => {
    window.addEventListener('resize', refreshHandler);

    return () => window.removeEventListener('resize', refreshHandler);
  }, [refreshHandler]);

  if (fullSize) {
    style.width = '100%';
  } else {
    style.overflow = 'auto';
  }

  useEffect(() => {
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [containerRef, resizeObserver]);

  return (
    <div ref={containerRef} style={style}>
      {content}
    </div>
  );
};

export default ScalingReport;
